// Ported from the native app's NotificationData.swift — the in-app
// notification center. The scanning half runs in the background service
// worker (which also mirrors each new notification to a system
// chrome.notifications toast); this store is the shared persisted list.

import { Emitter, onStorageChange, storageGet, storageSet } from "./storage.js";
import { tradingSessions, status } from "./session-math.js";
import { upcomingEconEvents } from "./econ-calendar.js";
import { chartLineStore, isHorizontal, linePriceAt } from "./chart-lines.js";
import type { PriceAlert, TickerSnapshot } from "./market-data.js";

/** Structural view of WatchlistStore — lets the background worker pass a
 * lightweight facade with only the symbols it actually fetched prices for. */
export interface WatchlistLike {
  snapshots: Record<string, TickerSnapshot>;
  alerts: Record<string, PriceAlert>;
  removeAlert(symbol: string): Promise<void> | void;
}

export type NotificationCategory =
  | "productUpdate"
  | "watchlistExtreme"
  | "econEvent"
  | "sessionChange"
  | "priceAlert"
  | "strategySignal";

export const categoryIcon: Record<NotificationCategory, string> = {
  watchlistExtreme: "📈",
  productUpdate: "✨",
  econEvent: "📅",
  sessionChange: "🌐",
  priceAlert: "🔔",
  strategySignal: "🧪",
};

export interface AppNotification {
  id: string;
  date: number; // epoch ms
  title: string;
  message: string;
  targetPage: number;
  category: NotificationCategory;
}

const NOTIFICATIONS_KEY = "appNotifications";
const SEEN_KEY = "notifiedEventIDs";
const SESSIONS_BASELINE_KEY = "previousActiveSessions";

export class NotificationStore {
  notifications: AppNotification[] = [];
  readonly changed = new Emitter();
  private seenEventIDs = new Set<string>();
  /** Baseline for session transitions — null until the first scan, so a
   * launch mid-session doesn't fire a false "just started". Persisted, since
   * MV3 service workers restart constantly. */
  private previousActiveSessions: string[] | null = null;
  /** Called for each genuinely new notification (service worker uses this
   * to raise a chrome.notifications toast). */
  onNew: ((n: AppNotification) => void) | null = null;

  async init(): Promise<void> {
    this.notifications = await storageGet<AppNotification[]>(NOTIFICATIONS_KEY, []);
    this.seenEventIDs = new Set(await storageGet<string[]>(SEEN_KEY, []));
    this.previousActiveSessions = await storageGet<string[] | null>(SESSIONS_BASELINE_KEY, null);
    onStorageChange<AppNotification[]>(NOTIFICATIONS_KEY, (v) => {
      this.notifications = v ?? [];
      this.changed.emit();
    });
    this.changed.emit();
  }

  private async persist(): Promise<void> {
    await storageSet(NOTIFICATIONS_KEY, this.notifications);
    await storageSet(SEEN_KEY, [...this.seenEventIDs]);
  }

  /** Public entry point for other systems (e.g. strategy signal scans). */
  async push(eventID: string, title: string, message: string, targetPage: number, category: NotificationCategory): Promise<void> {
    await this.addIfNew(eventID, title, message, targetPage, category);
  }

  async dismiss(id: string): Promise<void> {
    this.notifications = this.notifications.filter((n) => n.id !== id);
    await this.persist();
    this.changed.emit();
  }

  /** Each unique event id only ever produces one notification, even across
   * restarts — "tell me about new things", not a repeating reminder. */
  private async addIfNew(
    eventID: string,
    title: string,
    message: string,
    targetPage: number,
    category: NotificationCategory,
    date = Date.now()
  ): Promise<void> {
    if (this.seenEventIDs.has(eventID)) return;
    this.seenEventIDs.add(eventID);
    const notification: AppNotification = { id: crypto.randomUUID(), date, title, message, targetPage, category };
    this.notifications.unshift(notification);
    await this.persist();
    this.changed.emit();
    this.onNew?.(notification);
  }

  private async checkSessionChanges(clockPage: number, now: Date): Promise<void> {
    const active = tradingSessions.filter((s) => status(s, now).isActive).map((s) => s.name);
    const previous = this.previousActiveSessions;
    this.previousActiveSessions = active;
    await storageSet(SESSIONS_BASELINE_KEY, active);
    if (previous == null) return;

    const hourBucket = Math.floor(now.getTime() / 3600_000);
    for (const sessionName of active.filter((name) => !previous.includes(name))) {
      await this.addIfNew(
        `session-start-${sessionName}-${hourBucket}`,
        `${sessionName} session started`,
        `The ${sessionName} trading session is now active.`,
        clockPage,
        "sessionChange",
        now.getTime()
      );
    }
  }

  /**
   * Horizontal locked-line alarms reuse watchlist.alerts directly, so
   * they're covered by the price-alert loop — this only handles diagonal
   * trendlines, whose price target moves over time.
   */
  private async scanLineAlarms(watchlist: WatchlistLike, targetPage: number, now: Date): Promise<void> {
    for (const [chartKey, lines] of Object.entries(chartLineStore.linesByChart)) {
      for (const line of lines) {
        if (line.kind !== "trend" || !line.alarmEnabled || isHorizontal(line)) continue;
        const currentPrice = watchlist.snapshots[line.symbol]?.currentPrice;
        if (currentPrice == null) continue;
        const lineValueNow = linePriceAt(line, now.getTime());
        const isAboveNow = currentPrice >= lineValueNow;
        const updated = { ...line };
        if (line.alarmWasAbove != null && line.alarmWasAbove !== isAboveNow) {
          await this.addIfNew(
            `line-alert-${line.id}-${Math.floor(now.getTime() / 1000)}`,
            `${line.symbol} trendline alert`,
            `${line.symbol} crossed your trendline at ${lineValueNow.toFixed(2)} — now ${currentPrice.toFixed(2)}.`,
            targetPage,
            "priceAlert",
            now.getTime()
          );
          // One-shot, same as a regular price alert.
          updated.alarmEnabled = false;
          updated.alarmWasAbove = undefined;
        } else {
          updated.alarmWasAbove = isAboveNow;
        }
        await chartLineStore.updateLine(updated, chartKey);
      }
    }
  }

  async scan(
    watchlist: WatchlistLike,
    pages: { clockPage: number; watchlistPage: number }
  ): Promise<void> {
    const now = new Date();
    const hourBucket = Math.floor(now.getTime() / 3600_000);

    await this.checkSessionChanges(pages.clockPage, now);
    await this.scanLineAlarms(watchlist, pages.watchlistPage, now);

    for (const [symbol, alert] of Object.entries(watchlist.alerts)) {
      const price = watchlist.snapshots[symbol]?.currentPrice;
      if (price == null) continue;
      const isAboveNow = price >= alert.targetPrice;
      if (isAboveNow === alert.wasAbove) continue;
      await this.addIfNew(
        `alert-${symbol}-${alert.targetPrice}-${Math.floor(now.getTime() / 1000)}`,
        `${symbol} price alert`,
        `${symbol} crossed ${alert.targetPrice.toFixed(2)} — now ${price.toFixed(2)}.`,
        pages.watchlistPage,
        "priceAlert"
      );
      // One-shot: the alert did its job.
      await watchlist.removeAlert(symbol);
    }

    for (const [symbol, snapshot] of Object.entries(watchlist.snapshots)) {
      if (!snapshot.rows.length || snapshot.currentPrice == null) continue;
      const price = snapshot.currentPrice;
      const fractions = snapshot.rows.map((row) =>
        row.top > row.bottom ? Math.min(1, Math.max(0, (price - row.bottom) / (row.top - row.bottom))) : 0.5
      );
      if (fractions.every((f) => f >= 0.95)) {
        await this.addIfNew(
          `wl-top-${symbol}-${hourBucket}`,
          `${symbol} at range highs`,
          `${symbol} is at the top of its Bollinger Band across every timeframe — a possible overbought signal.`,
          pages.watchlistPage,
          "watchlistExtreme"
        );
      } else if (fractions.every((f) => f <= 0.05)) {
        await this.addIfNew(
          `wl-bottom-${symbol}-${hourBucket}`,
          `${symbol} at range lows`,
          `${symbol} is at the bottom of its Bollinger Band across every timeframe — a possible oversold signal.`,
          pages.watchlistPage,
          "watchlistExtreme"
        );
      }
    }

    for (const event of upcomingEconEvents(3)) {
      const minutesAway = (event.date.getTime() - now.getTime()) / 60000;
      if (minutesAway <= 0 || minutesAway > 60) continue;
      await this.addIfNew(
        `econ-${event.name}-${Math.floor(event.date.getTime() / 1000)}`,
        event.name,
        `${event.name} in ${Math.round(minutesAway)} minutes — expect volatility.`,
        pages.clockPage, // Markets page (calendar lives there)
        "econEvent"
      );
    }
  }
}

export const notificationStore = new NotificationStore();
